/**
 * Agent Registry
 * 
 * Defines the complete flow graph of all agents and their relationships.
 * This is the central configuration for the multi-agent system.
 */

import { AgentNode, VoiceGender } from './types';
import {
  createGreetingAgent,
  createWelcomeAgent,
  createRouterAgent,
  createFAQAgent,
  // Renewal flow (Option 3)
  createRenewalInputAgent,
  createRenewalVehicleAgent,
  createRenewalConfirmAgent,
  createRenewalResultAgent,
  // Accident flow (Option 2)
  createAccidentStartAgent,
  createAccidentCollectSubmitAgent,
  // Lost Report flow (Option 4)
  createLostReportUploadExtractAgent,
  createLostReportSearchAgent,
  createLostReportResultAgent,
} from './nodes';

/**
 * Agent flow graph showing valid transitions
 * 
 * Flow Diagram:
 * 
 * ┌─────────────────┐
 * │  GreetingAgent  │ (Entry Point - Language Detection)
 * └────────┬────────┘
 *          │ language_detected
 *          ▼
 * ┌─────────────────┐
 * │  WelcomeAgent   │ (Welcome Message)
 * └────────┬────────┘
 *          │ welcome_complete
 *          ▼
 * ┌─────────────────┐
 * │  RouterAgent    │◄────────────────────────────────────┐
 * └────────┬────────┘                                     │
 *          │                                              │
 *    ┌─────┼─────────┬──────────────┬────────────────────┐│
 *    │     │         │              │                     │
 *    ▼     │         ▼              ▼                     │
 * ┌──────┐ │  ┌────────────┐  ┌─────────────┐             │
 * │ FAQ  │ │  │AccidentFlow│  │LostReportFlow│            │
 * └──┬───┘ │  └────────────┘  └─────────────┘             │
 *    │     │         │              │                     │
 *    │     │         ▼              ▼                     │
 *    │     │  AccidentStart   LostReportUpload            │
 *    │     │         │              │                     │
 *    │     │         ▼              ▼                     │
 *    │     │  AccidentCollect LostReportExtract           │
 *    │     │         │              │                     │
 *    │     │         ▼              ▼                     │
 *    │     │  AccidentSubmit  LostReportSearch            │
 *    │     │         │              │                     │
 *    │     │         └──────────────┼──────────▶──────────┤
 *    │     │                        ▼                     │
 *    │     │                  LostReportResult            │
 *    │     │                        │                     │
 *    │     │                        └─────────────────────┤
 *    │     │                                              │
 *    │     ▼ option_3                                     │
 *    │ ┌───────────────────┐                              │
 *    │ │RenewalInputAgent  │                              │
 *    │ └────────┬──────────┘                              │
 *    │          │ report_found                            │
 *    │          ▼                                         │
 *    │ ┌───────────────────┐                              │
 *    │ │RenewalVehicleAgent│                              │
 *    │ └────────┬──────────┘                              │
 *    │          │ vehicle_selected                        │
 *    │          ▼                                         │
 *    │ ┌───────────────────┐                              │
 *    │ │RenewalConfirmAgent│                              │
 *    │ └────────┬──────────┘                              │
 *    │          │ submission_complete                     │
 *    │          ▼                                         │
 *    │ ┌───────────────────┐                              │
 *    │ │RenewalResultAgent │                              │
 *    │ └────────┬──────────┘                              │
 *    │          │                                         │
 *    └──────────┴─────────────────────────────────────────┘
 */

/**
 * Create all agents with the specified voice gender
 */
export function createAllAgents(voiceGender: VoiceGender = 'male'): Map<string, AgentNode> {
  const agents = new Map<string, AgentNode>();
  
  // Entry point
  agents.set('greeting_agent', createGreetingAgent(voiceGender));
  
  // Welcome message (after language detection)
  agents.set('welcome_agent', createWelcomeAgent(voiceGender));
  
  // Main router
  agents.set('router_agent', createRouterAgent(voiceGender));
  
  // FAQ flow (Option 1)
  agents.set('faq_agent', createFAQAgent(voiceGender));
  
  // Accident Reporting flow (Option 2) - 2 step process (collect+submit merged)
  agents.set('accident_start_agent', createAccidentStartAgent(voiceGender));
  agents.set('accident_collect_submit_agent', createAccidentCollectSubmitAgent(voiceGender));
  
  // Report Renewal flow (Option 3) - 4 step process
  agents.set('renewal_input_agent', createRenewalInputAgent(voiceGender));
  agents.set('renewal_vehicle_agent', createRenewalVehicleAgent(voiceGender));
  agents.set('renewal_confirm_agent', createRenewalConfirmAgent(voiceGender));
  agents.set('renewal_result_agent', createRenewalResultAgent(voiceGender));
  
  // Lost Report Replacement flow (Option 4) - 3 step process (upload+extract merged)
  agents.set('lost_report_upload_extract_agent', createLostReportUploadExtractAgent(voiceGender));
  agents.set('lost_report_search_agent', createLostReportSearchAgent(voiceGender));
  agents.set('lost_report_result_agent', createLostReportResultAgent(voiceGender));
  
  return agents;
}

/**
 * Get the initial agent ID (entry point)
 */
export function getInitialAgentId(): string {
  return 'greeting_agent';
}

/**
 * Get list of all agent IDs
 */
export function getAllAgentIds(): string[] {
  return [
    // Core
    'greeting_agent',
    'welcome_agent',
    'router_agent',
    'faq_agent',
    // Accident flow (Option 2)
    'accident_start_agent',
    'accident_collect_submit_agent',
    // Renewal flow (Option 3)
    'renewal_input_agent',
    'renewal_vehicle_agent',
    'renewal_confirm_agent',
    'renewal_result_agent',
    // Lost Report flow (Option 4)
    'lost_report_upload_extract_agent',
    'lost_report_search_agent',
    'lost_report_result_agent',
  ];
}

/**
 * Agent flow metadata for debugging and visualization
 */
export const agentFlowMetadata = {
  entryPoint: 'greeting_agent',
  
  flows: {
    greeting: {
      agents: ['greeting_agent', 'welcome_agent'],
      description: 'Language detection and welcome message',
    },
    router: {
      agents: ['router_agent'],
      description: 'Main menu and intent routing',
    },
    faq: {
      agents: ['faq_agent'],
      description: 'FAQ and question answering (Option 1)',
    },
    accident: {
      agents: [
        'accident_start_agent',
        'accident_collect_submit_agent',
      ],
      description: 'Accident reporting multi-step process (Option 2)',
    },
    renewal: {
      agents: [
        'renewal_input_agent',
        'renewal_vehicle_agent', 
        'renewal_confirm_agent',
        'renewal_result_agent',
      ],
      description: 'Report renewal multi-step process (Option 3)',
    },
    lostReport: {
      agents: [
        'lost_report_upload_extract_agent',
        'lost_report_search_agent',
        'lost_report_result_agent',
      ],
      description: 'Lost report replacement multi-step process (Option 4)',
    },
  },
  
  transitions: [
    // Greeting to Welcome to Router
    { from: 'greeting_agent', to: 'welcome_agent', trigger: 'language_detected' },
    { from: 'welcome_agent', to: 'router_agent', trigger: 'welcome_complete' },
    
    // Router to flows
    { from: 'router_agent', to: 'faq_agent', trigger: 'option_1' },
    { from: 'router_agent', to: 'accident_start_agent', trigger: 'option_2' },
    { from: 'router_agent', to: 'renewal_input_agent', trigger: 'option_3' },
    { from: 'router_agent', to: 'lost_report_upload_extract_agent', trigger: 'option_4' },
    
    // FAQ flow
    { from: 'faq_agent', to: 'router_agent', trigger: 'back_to_menu' },
    { from: 'faq_agent', to: 'renewal_input_agent', trigger: 'switch_renewal' },
    { from: 'faq_agent', to: 'accident_start_agent', trigger: 'switch_accident' },
    { from: 'faq_agent', to: 'lost_report_upload_extract_agent', trigger: 'switch_lost_report' },
    // Cross-flow navigation from FAQ
    { from: 'faq_agent', to: 'accident_start_agent', trigger: 'start_accident_reporting' },
    { from: 'faq_agent', to: 'renewal_input_agent', trigger: 'start_renewal' },
    { from: 'faq_agent', to: 'lost_report_upload_extract_agent', trigger: 'start_lost_report_search' },
    
    // Accident flow
    { from: 'accident_start_agent', to: 'accident_collect_submit_agent', trigger: 'proceed_to_collect' },
    { from: 'accident_start_agent', to: 'router_agent', trigger: 'cancel' },
    { from: 'accident_collect_submit_agent', to: 'accident_start_agent', trigger: 'new_report' },
    { from: 'accident_collect_submit_agent', to: 'router_agent', trigger: 'finish' },
    { from: 'accident_collect_submit_agent', to: 'router_agent', trigger: 'cancel' },
    // Cross-flow navigation from Accident
    { from: 'accident_collect_submit_agent', to: 'renewal_input_agent', trigger: 'start_renewal' },
    { from: 'accident_collect_submit_agent', to: 'lost_report_upload_extract_agent', trigger: 'start_lost_report_search' },
    { from: 'accident_collect_submit_agent', to: 'faq_agent', trigger: 'go_to_faq' },
    
    // Renewal flow
    { from: 'renewal_input_agent', to: 'renewal_vehicle_agent', trigger: 'report_found' },
    { from: 'renewal_input_agent', to: 'router_agent', trigger: 'cancel' },
    { from: 'renewal_vehicle_agent', to: 'renewal_confirm_agent', trigger: 'vehicle_selected' },
    { from: 'renewal_vehicle_agent', to: 'router_agent', trigger: 'cancel' },
    { from: 'renewal_confirm_agent', to: 'renewal_result_agent', trigger: 'submission_complete' },
    { from: 'renewal_confirm_agent', to: 'router_agent', trigger: 'cancel' },
    { from: 'renewal_result_agent', to: 'renewal_input_agent', trigger: 'new_request' },
    { from: 'renewal_result_agent', to: 'router_agent', trigger: 'finish' },
    // Cross-flow navigation from Renewal Result
    { from: 'renewal_result_agent', to: 'accident_start_agent', trigger: 'start_accident_reporting' },
    { from: 'renewal_result_agent', to: 'lost_report_upload_extract_agent', trigger: 'start_lost_report_search' },
    { from: 'renewal_result_agent', to: 'faq_agent', trigger: 'go_to_faq' },
    
    // Lost Report flow
    { from: 'lost_report_upload_extract_agent', to: 'lost_report_search_agent', trigger: 'data_confirmed' },
    { from: 'lost_report_upload_extract_agent', to: 'router_agent', trigger: 'cancel' },
    { from: 'lost_report_search_agent', to: 'lost_report_result_agent', trigger: 'search_complete' },
    { from: 'lost_report_search_agent', to: 'lost_report_upload_extract_agent', trigger: 'restart' },
    { from: 'lost_report_search_agent', to: 'router_agent', trigger: 'cancel' },
    { from: 'lost_report_result_agent', to: 'lost_report_upload_extract_agent', trigger: 'new_search' },
    { from: 'lost_report_result_agent', to: 'lost_report_search_agent', trigger: 'try_different_dates' },
    { from: 'lost_report_result_agent', to: 'renewal_input_agent', trigger: 'start_renewal' },
    { from: 'lost_report_result_agent', to: 'router_agent', trigger: 'finish' },
    // Cross-flow navigation from Lost Report Result
    { from: 'lost_report_result_agent', to: 'accident_start_agent', trigger: 'start_accident_reporting' },
    { from: 'lost_report_result_agent', to: 'faq_agent', trigger: 'go_to_faq' },
  ],
};

/**
 * Validate that all transitions reference valid agents
 */
export function validateRegistry(agents: Map<string, AgentNode>): string[] {
  const errors: string[] = [];
  const agentIds = new Set(agents.keys());
  
  for (const [agentId, agent] of agents) {
    // Check exit transitions
    for (const [transitionName, transition] of agent.exitGuardrails.transitions) {
      if (!agentIds.has(transition.target.agentId)) {
        errors.push(
          `Agent '${agentId}' has transition '${transitionName}' to unknown agent '${transition.target.agentId}'`
        );
      }
    }
    
    // Check fallback
    if (agent.exitGuardrails.fallback && !agentIds.has(agent.exitGuardrails.fallback.agentId)) {
      errors.push(
        `Agent '${agentId}' has fallback to unknown agent '${agent.exitGuardrails.fallback.agentId}'`
      );
    }
  }
  
  return errors;
}

